import type { StoryVersionType, TreeModel } from 'types/story';
import { componentWalkFull } from 'utils/blocks/component-walk-full';
import { getSymbolStyleLayers, getExpandedStyleElement } from 'utils/blocks/get-symbol-style-layers';
import { isSymbol } from 'utils/blocks/symbol';
import type { DesignDefinition } from './types';
import { collectEffectiveStyleAssignments, type EffectiveStyleAssignments } from './preset-styles';

/** Read mounted assignments; symbol definitions and unused presets do not count as styled blocks. */
export function collectStoryStyleAssignments({
	data,
	design,
}: {
	data: StoryVersionType['data'];
	design?: DesignDefinition;
}): { element: TreeModel; assignments: EffectiveStyleAssignments }[] {
	const symbols = data.symbols ?? {};
	const result = new Map<string, { element: TreeModel; assignments: EffectiveStyleAssignments }>();
	const composedInstances = new Set<string>();
	const walk = (elements: Parameters<typeof componentWalkFull>[0]['elements']) => {
		componentWalkFull({
			elements,
			symbols,
			storyElements: data.elements,
			callback: ({ component, stopCurrent }) => {
				if (
					isSymbol(component) &&
					!component.symbol.childId &&
					!composedInstances.has(component.symbol.instanceId)
				) {
					const expanded = getExpandedStyleElement(component, symbols);
					if (expanded) {
						composedInstances.add(component.symbol.instanceId);
						stopCurrent();
						walk([expanded]);
						return;
					}
				}
				const element = getExpandedStyleElement(component, symbols) ?? component;
				result.set(element._id, {
					element,
					assignments: collectEffectiveStyleAssignments({
						design,
						presetId: element.uiConfig.componentProps.designPresetId,
						mediaQuery: data.mediaQuery,
						layers: getSymbolStyleLayers(component, symbols),
					}),
				});
			},
		});
	};
	walk(data.elements);
	for (const step of data.steps) for (const card of step.cards) walk(card.elements);
	return [...result.values()];
}
